import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, Filter, Mail, FileText, Edit2, Trash2, MoreVertical,
  Eye, FileCheck, DollarSign, CheckCircle, Download, Send, UserPlus,
  Building2, Landmark, UserCheck, BookOpen, MapPin, ArrowUpDown, Calendar,
  Merge, X, Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';

// Tous les statuts CRM (sauf 'client' qui est dans l'onglet Clients)
export const ALL_CRM_STATUTS = [
  'nouveau', 'contacte', 'interesse', 'peu_interesse', 
  'demarche_inscription', 'en_cours_inscription', 'candidat_inscrit', 'stagiaire', 'perdu'
];
const STATUTS_CANDIDAT = ['candidat_inscrit', 'stagiaire'];
// Un contact devient « candidat » dès qu'il est inscrit sur l'application
export const isCandidatCRM = (p) => !!p.candidat_user_id || STATUTS_CANDIDAT.includes(p.statut);
const MODES = {
  prospects: { titre: 'Prospects', unite: 'prospect', match: (p) => !isCandidatCRM(p) },
  candidats: { titre: 'Candidats', unite: 'candidat', match: isCandidatCRM },
};

const UnifiedCRMTable = ({
  prospects,
  config,
  onViewDetails,
  onEdit,
  onEmail,
  onQuote,
  onProgramme,
  onEnregistrerPaiement,
  fetchData,
  onValidateClient,
  mode = 'prospects'
}) => {
  const modeInfo = MODES[mode];
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [centreFilter, setCentreFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeTarget, setMergeTarget] = useState(null);

  // Fonction pour gérer le tri
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // Toggle direction si même colonne
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // Nouvelle colonne: commencer par desc pour date, asc pour les autres
      return { key, direction: key === 'date' ? 'desc' : 'asc' };
    });
  };

  // Icône de tri pour les en-têtes
  const SortIcon = ({ columnKey }) => {
    const isActive = sortConfig.key === columnKey;
    return (
      <span className="inline-flex items-center ml-1">
        <ArrowUpDown className={`h-3 w-3 ${isActive ? 'text-[#8DC63F]' : 'text-slate-400'}`} />
        {isActive && (
          <span className="text-[10px] text-[#8DC63F] ml-0.5">
            {sortConfig.direction === 'desc' ? '↓' : '↑'}
          </span>
        )}
      </span>
    );
  };

  const getStatusBadge = (statut) => {
    const statusInfo = config.statuts.find(s => s.value === statut) || { label: statut, color: '#6B7280' };
    return (
      <Badge
        style={{ backgroundColor: statusInfo.color, color: 'white' }}
        className="text-xs"
      >
        {statusInfo.label}
      </Badge>
    );
  };

  const getDevisStatusBadge = (devisStatut) => {
    const statusMap = {
      brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
      envoye: { label: 'Envoyé', color: 'bg-blue-100 text-blue-700' },
      accepte: { label: 'Accepté', color: 'bg-green-100 text-green-700' },
      signe: { label: 'Signé', color: 'bg-emerald-100 text-emerald-700' },
      refuse: { label: 'Refusé', color: 'bg-red-100 text-red-700' }
    };
    const status = statusMap[devisStatut] || { label: devisStatut, color: 'bg-slate-100 text-slate-600' };
    return <Badge className={`${status.color} text-xs`}>{status.label}</Badge>;
  };

  // Filtrer les prospects (exclure les clients qui sont dans l'onglet séparé)
  const crmProspects = prospects.filter(p => ALL_CRM_STATUTS.includes(p.statut) && modeInfo.match(p));

  // Filtrage par recherche, statut et centre
  const filteredProspects = crmProspects.filter(p => {
    const matchesSearch = searchTerm === '' ||
      `${p.nom} ${p.prenom} ${p.email}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.statut === statusFilter;
    const matchesCentre = centreFilter === 'all' || p.centre === centreFilter;
    return matchesSearch && matchesStatus && matchesCentre;
  });

  // Tri des prospects
  const sortedProspects = [...filteredProspects].sort((a, b) => {
    const { key, direction } = sortConfig;
    let comparison = 0;

    switch (key) {
      case 'date':
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        comparison = dateA - dateB;
        break;
      case 'nom':
        const nomA = `${a.nom || ''} ${a.prenom || ''}`.toLowerCase();
        const nomB = `${b.nom || ''} ${b.prenom || ''}`.toLowerCase();
        comparison = nomA.localeCompare(nomB, 'fr');
        break;
      case 'statut':
        comparison = (a.statut || '').localeCompare(b.statut || '', 'fr');
        break;
      case 'centre':
        comparison = (a.centre || '').localeCompare(b.centre || '', 'fr');
        break;
      case 'relances':
        comparison = (a.nb_relances || 0) - (b.nb_relances || 0);
        break;
      default:
        comparison = 0;
    }

    return direction === 'desc' ? -comparison : comparison;
  });

  // Stats globales (filtrées par centre si sélectionné)
  const statsProspects = centreFilter === 'all' ? crmProspects : crmProspects.filter(p => p.centre === centreFilter);
  const stats = {
    total: statsProspects.length,
    by_status: Object.fromEntries(
      ALL_CRM_STATUTS.map(s => [s, statsProspects.filter(p => p.statut === s).length])
    )
  };

  const handleStatusChange = async (prospectId, newStatus) => {
    try {
      await axios.put(`/crm/prospects/${prospectId}`, { statut: newStatus });
      if (newStatus === 'stagiaire') {
        toast.success('Prospect passe en client (stagiaire) !');
        if (onValidateClient) onValidateClient();
      } else {
        toast.success('Statut mis a jour');
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour du statut');
    }
  };

  const handleDelete = async (prospectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) return;
    try {
      await axios.delete(`/crm/prospects/${prospectId}`);
      toast.success('Contact supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleValidateDevis = async (prospectId, devisId) => {
    try {
      await axios.patch(`/crm/prospects/${prospectId}/devis/${devisId}/validate`);
      toast.success('Devis validé !');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleDeleteDevis = async (prospectId, devisId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) return;
    try {
      await axios.delete(`/crm/prospects/${prospectId}/devis/${devisId}`);
      toast.success('Devis supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadDevis = async (prospectId, devisId) => {
    try {
      const prospect = prospects.find(p => p.prospect_id === prospectId);
      const response = await axios.get(
        `/crm/prospects/${prospectId}/devis/${devisId}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prospect?.nom || ''}_${prospect?.prenom || ''}_devis.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Devis téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendDevis = async (prospectId, devisId) => {
    try {
      const response = await axios.post(`/crm/prospects/${prospectId}/devis/${devisId}/send`);
      toast.success(`Devis envoyé à ${response.data.email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleBasculerClient = async (prospect) => {
    if (!window.confirm(`Passer ${prospect.prenom || ''} ${prospect.nom || ''} en client ? Il basculera dans l'onglet « Clients ».`)) return;
    const prospectId = prospect.prospect_id;
    try {
      await axios.put(`/crm/prospects/${prospectId}`, { statut: 'client' });
      toast.success('Contact validé et basculé en client !');
      await fetchData();
      if (onValidateClient) {
        onValidateClient();
      }
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    const sourceP = mergeSource;
    const targetP = mergeTarget;
    const kept = Math.min(sourceP.numero_candidat || 9999, targetP.numero_candidat || 9999);
    try {
      const [tId, sId] = (sourceP.numero_candidat || 9999) <= (targetP.numero_candidat || 9999)
        ? [sourceP.prospect_id, targetP.prospect_id]
        : [targetP.prospect_id, sourceP.prospect_id];
      await axios.post(`/crm/prospects/${tId}/merge/${sId}`);
      toast.success(`Fusion réussie ! N° candidat conservé : ${kept}`);
      setMergeSource(null);
      setMergeSearch('');
      setMergeTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la fusion');
    }
  };

  const mergeTargets = mergeSource ? prospects.filter(p =>
    p.prospect_id !== mergeSource.prospect_id &&
    (mergeSearch === '' || `${p.nom} ${p.prenom} ${p.email}`.toLowerCase().includes(mergeSearch.toLowerCase()))
  ).slice(0, 8) : [];

  // Statuts disponibles pour le dropdown de changement de statut (SANS 'client')
  // Le statut 'client' ne peut être attribué que via le bouton "Valider"
  const availableStatuts = config.statuts.filter(s =>
    ALL_CRM_STATUTS.includes(s.value)
  );

  // Vérifier si un prospect a un devis validé (accepté ou signé)
  const hasValidatedDevis = (prospect) => {
    return (prospect.devis_historique || []).some(d => 
      d.statut === 'accepte' || d.statut === 'signe'
    );
  };

  // Bouton Valider : candidats inscrits ou stagiaires (indépendant de l'acceptation du devis)
  const canBeValidatedAsClient = (prospect) => STATUTS_CANDIDAT.includes(prospect.statut);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-[#8DC63F]" />
          <h2 className="text-lg font-semibold text-slate-800" data-testid={`crm-${mode}-title`}>{modeInfo.titre}</h2>
          <Badge variant="outline" className="ml-2" data-testid={`crm-${mode}-count`}>
            {stats.total} {modeInfo.unite}{stats.total > 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Stats Cards - Sans CA prévisionnel */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {config.statuts
          .filter(s => ALL_CRM_STATUTS.includes(s.value) && (
            mode === 'candidats'
              ? STATUTS_CANDIDAT.includes(s.value) || stats.by_status[s.value] > 0
              : !STATUTS_CANDIDAT.includes(s.value)
          ))
          .map(s => (
            <Card 
              key={s.value} 
              className={`bg-white border-slate-100 cursor-pointer transition-all hover:shadow-md ${
                statusFilter === s.value ? 'ring-2 ring-offset-1' : ''
              }`}
              style={{ 
                '--tw-ring-color': statusFilter === s.value ? s.color : 'transparent'
              }}
              onClick={() => setStatusFilter(statusFilter === s.value ? 'all' : s.value)}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-medium truncate">{s.label}</p>
                    <p className="text-xl font-bold" style={{ color: s.color }}>
                      {stats.by_status[s.value] || 0}
                    </p>
                  </div>
                  <div
                    className="h-6 w-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${s.color}20` }}
                  >
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, prénom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                data-testid="crm-search-input"
              />
            </div>
            <Select value={centreFilter} onValueChange={setCentreFilter}>
              <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="crm-centre-filter">
                <MapPin className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Centre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les centres</SelectItem>
                <SelectItem value="atlantique">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                    Atlantique
                  </div>
                </SelectItem>
                <SelectItem value="occitanie">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-orange-500" />
                    Occitanie
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px] h-9" data-testid="crm-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {config.statuts
                  .filter(s => ALL_CRM_STATUTS.includes(s.value) && (mode === 'candidats' || s.value !== 'stagiaire'))
                  .map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {(statusFilter !== 'all' || centreFilter !== 'all' || sortConfig.key !== 'date') && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setStatusFilter('all'); setCentreFilter('all'); setSortConfig({ key: 'date', direction: 'desc' }); }}
                className="h-9"
              >
                Effacer filtres
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="unified-crm-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th 
                    className="text-left p-3 text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('date')}
                    data-testid="sort-date"
                  >
                    <div className="flex items-center">
                      Date
                      <SortIcon columnKey="date" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('nom')}
                    data-testid="sort-nom"
                  >
                    <div className="flex items-center">
                      Contact
                      <SortIcon columnKey="nom" />
                    </div>
                  </th>
                  <th className="text-left p-3 text-xs font-medium text-slate-600">Ville</th>
                  <th 
                    className="text-left p-3 text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('centre')}
                    data-testid="sort-centre"
                  >
                    <div className="flex items-center">
                      Centre
                      <SortIcon columnKey="centre" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('statut')}
                    data-testid="sort-statut"
                  >
                    <div className="flex items-center">
                      Statut
                      <SortIcon columnKey="statut" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 text-xs font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort('relances')}
                    data-testid="sort-relances"
                  >
                    <div className="flex items-center">
                      Relances
                      <SortIcon columnKey="relances" />
                    </div>
                  </th>
                  <th className="text-left p-3 text-xs font-medium text-slate-600">Devis</th>
                  <th className="text-left p-3 text-xs font-medium text-slate-600">Financement</th>
                  <th className="text-right p-3 text-xs font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortedProspects.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="p-8 text-center text-slate-500">
                      <div className="space-y-2">
                        <Users className="h-12 w-12 mx-auto text-slate-300" />
                        <p>Aucun contact trouvé</p>
                        {statusFilter !== 'all' && (
                          <p className="text-xs text-slate-400">
                            Essayez de modifier le filtre de statut
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedProspects.map(prospect => {
                    const devisValide = (prospect.devis_historique || []).find(
                      d => d.statut === 'accepte' || d.statut === 'signe'
                    );
                    const dernierDevis = (prospect.devis_historique || [])[0];
                    const canValidate = canBeValidatedAsClient(prospect);
                    
                    // Infos relances
                    const nbRelances = prospect.nb_relances || 0;
                    const derniereRelance = prospect.derniere_relance;
                    const derniereRelanceType = prospect.derniere_relance_type;
                    
                    // Couleur de fond selon le statut
                    const getBgColor = () => {
                      if (prospect.statut === 'stagiaire') return 'bg-green-50/50';
                      if (prospect.statut === 'en_cours_inscription') return 'bg-cyan-50/50';
                      if (prospect.centre === 'occitanie') return 'bg-orange-50/30';
                      return 'bg-blue-50/30';
                    };

                    return (
                      <tr
                        key={prospect.prospect_id}
                        className={`hover:bg-slate-50 ${getBgColor()}`}
                        data-testid={`crm-row-${prospect.prospect_id}`}
                      >
                        <td className="p-3">
                          <p className="text-xs text-slate-600">
                            {prospect.created_at 
                              ? new Date(prospect.created_at).toLocaleDateString('fr-FR')
                              : '-'
                            }
                          </p>
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">
                              {prospect.numero_candidat && (
                                <span className="text-xs font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded mr-2">N°{String(prospect.numero_candidat).padStart(4, '0')}</span>
                              )}
                              {prospect.prenom} {prospect.nom}
                            </p>
                            <p className="text-xs text-slate-500">{prospect.email}</p>
                            {prospect.telephone && (
                              <p className="text-xs text-slate-400">{prospect.telephone}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <p className="text-sm text-slate-600">{prospect.ville || '-'}</p>
                        </td>
                        <td className="p-3">
                          <Badge
                            variant="outline"
                            className={prospect.centre === 'occitanie'
                              ? 'bg-orange-100 text-orange-700 border-orange-300 text-xs'
                              : 'bg-blue-100 text-blue-700 border-blue-300 text-xs'
                            }
                          >
                            {prospect.centre === 'occitanie' ? 'Occitanie' : 'Atlantique'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Select
                            value={prospect.statut}
                            onValueChange={(val) => handleStatusChange(prospect.prospect_id, val)}
                          >
                            <SelectTrigger className="w-[160px] h-7 text-xs border-0 bg-transparent p-0">
                              {getStatusBadge(prospect.statut)}
                            </SelectTrigger>
                            <SelectContent>
                              {availableStatuts.map(s => (
                                <SelectItem key={s.value} value={s.value}>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="h-2 w-2 rounded-full"
                                      style={{ backgroundColor: s.color }}
                                    />
                                    {s.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        {/* Colonne Relances */}
                        <td className="p-3">
                          {nbRelances > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              <Badge 
                                className={`text-xs w-fit ${
                                  nbRelances >= 3 ? 'bg-red-100 text-red-700' :
                                  nbRelances === 2 ? 'bg-orange-100 text-orange-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}
                                title={derniereRelanceType ? `Dernier: ${derniereRelanceType}` : ''}
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                {nbRelances} relance{nbRelances > 1 ? 's' : ''}
                              </Badge>
                              {derniereRelance && (
                                <span className="text-[10px] text-slate-400">
                                  {new Date(derniereRelance).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs">Aucune</span>
                          )}
                        </td>
                        <td className="p-3">
                          {prospect.devis_historique?.length > 0 ? (
                            <div className="flex items-center gap-2">
                              <Badge
                                className={`cursor-pointer text-xs ${
                                  devisValide 
                                    ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' 
                                    : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                                }`}
                                onClick={() => onViewDetails(prospect)}
                              >
                                <FileCheck className="h-3 w-3 mr-1" />
                                {prospect.devis_historique.length}
                              </Badge>
                              {dernierDevis && dernierDevis.statut !== 'brouillon' && getDevisStatusBadge(dernierDevis.statut)}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          {prospect.recherche_financement ? (
                            <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50 text-xs">
                              <DollarSign className="h-3 w-3 mr-1" />
                              Recherche
                            </Badge>
                          ) : prospect.mode_financement ? (
                            <Badge variant="outline" className="text-slate-600 border-slate-300 bg-slate-50 text-xs">
                              {prospect.mode_financement}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            {/* Bouton Valider (basculer en client) si conditions remplies */}
                            {canValidate && (
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleBasculerClient(prospect)}
                                data-testid={`validate-client-${prospect.prospect_id}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Valider
                              </Button>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => onViewDetails(prospect)}
                              data-testid={`view-${prospect.prospect_id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-${prospect.prospect_id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {mode === 'candidats' && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => handleBasculerClient(prospect)}
                                      className="text-emerald-700"
                                      data-testid={`passer-client-${prospect.prospect_id}`}
                                    >
                                      <UserCheck className="h-4 w-4 mr-2" />
                                      Passer en client
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => onEdit(prospect)}>
                                  <Edit2 className="h-4 w-4 mr-2" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => onEmail(prospect)}>
                                  <Send className="h-4 w-4 mr-2" />
                                  Envoyer un email
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onQuote(prospect)} data-testid={`generer-devis-${prospect.prospect_id}`}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  Générer un devis
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onProgramme(prospect)}>
                                  <BookOpen className="h-4 w-4 mr-2" />
                                  Programme de formation
                                </DropdownMenuItem>
                                
                                {/* Télécharger dernier devis (ZIP si multi-payeurs) */}
                                {prospect.devis_historique?.length > 0 && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDownloadDevis(prospect.prospect_id, dernierDevis.devis_id)}>
                                      <Download className="h-4 w-4 mr-2" />
                                      Télécharger dernier devis
                                    </DropdownMenuItem>
                                  </>
                                )}
                                
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => { setMergeSource(prospect); setMergeSearch(''); }}
                                  data-testid={`merge-${prospect.prospect_id}`}
                                >
                                  <Merge className="h-4 w-4 mr-2" />
                                  Fusionner avec...
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(prospect.prospect_id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Supprimer contact
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info sur le bouton Valider */}
      <div className="text-xs text-slate-500 text-center">
        {mode === 'candidats'
          ? <>Le bouton "Valider" apparaît pour les candidats inscrits et stagiaires ; l'action "Passer en client" (menu ⋮) est disponible pour tous les candidats. Le passage est aussi automatique à la signature du devis ou à la conversion en stagiaire.</>
          : <>Dès qu'un prospect s'inscrit sur l'application (ou passe au statut "Candidat inscrit"), il passe dans l'onglet "Candidats".</>}
      </div>

      {/* Dialogue de fusion */}
      {mergeSource && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="merge-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Fusionner un contact</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Source : <span className="font-mono bg-slate-100 px-1 rounded">N°{String(mergeSource.numero_candidat || 0).padStart(4, '0')}</span> {mergeSource.prenom} {mergeSource.nom}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setMergeSource(null); setMergeTarget(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-600 mb-3">Sélectionnez le contact à fusionner. Le <strong>numéro candidat le plus ancien</strong> sera conservé.</p>
              <Input
                placeholder="Rechercher par nom, prénom ou email..."
                value={mergeSearch}
                onChange={(e) => { setMergeSearch(e.target.value); setMergeTarget(null); }}
                className="mb-3"
                data-testid="merge-search-input"
                autoFocus
              />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {mergeTargets.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {mergeSearch ? 'Aucun contact trouvé' : 'Tapez pour rechercher un contact'}
                  </p>
                ) : mergeTargets.map(t => (
                  <button
                    key={t.prospect_id}
                    className={`w-full text-left p-2.5 rounded-lg border transition-colors flex items-center justify-between ${
                      mergeTarget?.prospect_id === t.prospect_id
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300'
                        : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    onClick={() => setMergeTarget(t)}
                    data-testid={`merge-target-${t.prospect_id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {t.numero_candidat && (
                          <span className="text-xs font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded mr-2">N°{String(t.numero_candidat).padStart(4, '0')}</span>
                        )}
                        {t.prenom} {t.nom}
                      </p>
                      <p className="text-xs text-slate-500">{t.email}</p>
                    </div>
                    {mergeTarget?.prospect_id === t.prospect_id ? (
                      <Check className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Merge className="h-4 w-4 text-slate-400" />
                    )}
                  </button>
                ))}
              </div>
              {mergeTarget && (
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-2">
                    Fusionner <strong>{mergeSource.prenom} {mergeSource.nom}</strong> avec <strong>{mergeTarget.prenom} {mergeTarget.nom}</strong>
                  </p>
                  <Button
                    onClick={handleMerge}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-confirm-merge"
                  >
                    <Merge className="h-4 w-4 mr-2" />
                    Confirmer la fusion
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedCRMTable;
